import React from "react";
import {
  Grid,
  MenuItem,
  Typography,
  MenuList,
  CircularProgress,
  Box,
  Container
} from "@material-ui/core";

import WithAuth from "../containers/WithAuth";
import { inject, observer } from "mobx-react";
import { USER_PERMISSIONS } from "../utils/authConstants";
import styled from "styled-components";
import CustomClipVideoEditor from "../components/CustomClipVideoEditor";
import { withStyles } from "@material-ui/core/styles";
import Calendar from "../components/Calendar";
import dayjs from "dayjs";
import { toast } from "react-toastify";

const Wrapper = styled.div`
  padding: 0 2rem;
`;

const SelectableEventMenuItem = withStyles({
  root: {
    display: "inline-block",
    width: "100%",
    "&:hover": {
      color: "#FFFFFF",
      backgroundColor: "#002D72 !important"
    },
    borderRadius: "1rem",
    border: "1px solid #002D72"
  },
  selected: {
    color: "#FFFFFF",
    backgroundColor: "#002D72 !important"
  }
})(MenuItem);

const EventNotStartedToastMessage = ({ assetName, programStartTime }) => {
  return (
    <Box
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "start",
        justifyContent: "center",
        gap: "0.5rem"
      }}
    >
      <Box>
        <Typography style={{ fontSize: "1rem", fontWeight: 700 }}>
          Event Has Not Started
        </Typography>
        <Typography style={{ fontSize: "0.75rem" }}>{assetName}</Typography>
      </Box>
      <Typography style={{ fontSize: "0.75rem" }}>
        <span style={{ fontWeight: 600 }}>Start Time: </span>
        {dayjs(programStartTime).format("ddd, MMM D, YYYY h:mm A")}
      </Typography>
    </Box>
  );
};

class CustomAdHocClipEditor extends React.Component {
  componentWillMount() {
    this.initialize();
  }

  componentWillUnmount() {
    const { createCustomClipStore } = this.props;
    createCustomClipStore.reset();
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (prevProps.location.search !== this.props.location.search) {
      this.initialize();
    }
  }

  initialize() {
    const { createCustomClipStore, adhocMetadataStore } = this.props;
    const params = new URLSearchParams(this.props.location.search);
    const selectedDate = params.get("date");

    if (selectedDate) {
      adhocMetadataStore.date = dayjs(selectedDate).toDate();
    }

    const assetName = params.get("assetName");
    const feedType = params.get("feed");
    const playbackUrl = params.get("playbackUrl");
    const assetId = params.get("assetId");
    if (assetName && playbackUrl && feedType && assetId) {
      createCustomClipStore.initializeAdHoc(
        feedType,
        assetName,
        playbackUrl,
        assetId
      );
    }
  }

  render() {
    const {
      createCustomClipStore,
      adhocMetadataStore,
      routerStore
    } = this.props;

    const params = new URLSearchParams(this.props.location.search);
    const selectedAssetId = parseInt(params.get("assetId"), 10);

    const handleOnDateChange = date => {
      adhocMetadataStore.setDate(date);
    };

    const handleEventClicked = event => {
      if (!selectedAssetId || selectedAssetId !== event.assetId) {
        if (dayjs(new Date()).isBefore(event.programStartTime)) {
          createCustomClipStore.reset();
          routerStore.history.push(`/custom-clips/adhoc`);
          toast.warning(
            <EventNotStartedToastMessage
              programStartTime={event.programStartTime}
              assetName={event.assetName}
            />,
            {
              position: "top-right",
              autoClose: 3000
            }
          );
        } else {
          routerStore.history.push(
            `/custom-clips/adhoc?assetId=${event.assetId}&assetName=${
              event.assetName
            }&playbackUrl=${event.playbackUrl}&feed=${
              event.feedType
            }&date=${dayjs(adhocMetadataStore.date).format("YYYY-MM-DD")}`
          );
        }
      }
    };

    let editorElement = (
      <Typography variant="h5" style={{ padding: "1rem", textAlign: "center" }}>
        Select an Event to Create Custom Clip
      </Typography>
    );

    if (createCustomClipStore.fetching || createCustomClipStore.selectedAsset) {
      editorElement = <CustomClipVideoEditor />;
    }

    return (
      <Wrapper>
        <Grid container style={{ marginTop: 20 }} spacing={4}>
          <Grid
            item
            xs={4}
            style={{
              textAlign: "center",
              borderRight: "1px solid rgba(0, 0, 0, 0.12)",
              paddingRight: 0,
              paddingLeft: 0,
              height: "90vh",
              overflowY: "scroll"
            }}
          >
            <Calendar
              date={adhocMetadataStore.date}
              setDate={handleOnDateChange}
            />
            {adhocMetadataStore.loadingEvents ? (
              <div
                style={{
                  height: "100vh",
                  alignContent: "top",
                  marginTop: "2rem"
                }}
              >
                <CircularProgress />
              </div>
            ) : adhocMetadataStore.cerebroEvents.length > 0 ? (
              <MenuList
                style={{
                  marginTop: "0.5rem",
                  textAlign: "left",
                  display: "flex",
                  padding: "0.25rem",
                  flexDirection: "column",
                  gap: "0.5rem"
                }}
              >
                {adhocMetadataStore.cerebroEvents.map(e => {
                  return (
                    <SelectableEventMenuItem
                      key={e.assetId}
                      selected={selectedAssetId === e.assetId}
                      onClick={() => handleEventClicked(e)}
                    >
                      <Container>
                        <Typography
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                          }}
                        >
                          {e.title}
                        </Typography>
                        <Typography>{`Start Time: ${dayjs(
                          e.programStartTime
                        ).format("hh:mm A")}`}</Typography>
                        <Typography>{`Event Type: ${e.type}`}</Typography>
                      </Container>
                    </SelectableEventMenuItem>
                  );
                })}
              </MenuList>
            ) : (
              <div
                style={{
                  height: "100vh",
                  alignContent: "top",
                  marginTop: "2rem"
                }}
              >
                <Typography>No Events Today</Typography>
              </div>
            )}
          </Grid>
          <Grid
            item
            xs={8}
            style={{
              paddingTop: 0
            }}
          >
            {editorElement}
          </Grid>
        </Grid>
      </Wrapper>
    );
  }
}

export default WithAuth(
  inject(
    "routerStore",
    "createCustomClipStore",
    "adhocMetadataStore"
  )(observer(CustomAdHocClipEditor)),
  USER_PERMISSIONS.CAN_ACCESS_ADHOC_CLIPPING
);



// WEBPACK FOOTER //
// ./src/pages/CustomAdHocClipEditor.js